import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useToast } from "./toast";
import { useAchievements } from "./achievements";
import { play } from "@/lib/sound";

export type SliderKey = "curiosity" | "overthinking" | "sleep" | "tolerance";
export type Sliders = Record<SliderKey, number>;

export const SLIDER_DEFAULTS: Sliders = { curiosity: 70, overthinking: 69, sleep: 30, tolerance: 60 };
export const HIGH = 70;
export const LOW = 30;

export interface Modes {
    chaotic: boolean;
    bedtime: boolean;
    impatient: boolean;
}

interface SettingsApi {
    sliders: Sliders;
    setSlider: (k: SliderKey, v: number) => void;
    reset: () => void;
    modes: Modes;
}

const Ctx = createContext<SettingsApi | null>(null);

function loadSliders(): Sliders {
    try {
        const raw = localStorage.getItem("krithios:sliders");
        if (raw) return { ...SLIDER_DEFAULTS, ...(JSON.parse(raw) as Sliders) };
    } catch {
        /* defaults */
    }
    return { ...SLIDER_DEFAULTS };
}

export function SettingsProvider({ children }: { children: ReactNode }) {
    const [sliders, setSliders] = useState<Sliders>(loadSliders);
    const { push } = useToast();
    const { unlock } = useAchievements();

    const setSlider = useCallback((k: SliderKey, v: number) => {
        setSliders((prev) => {
            const next = { ...prev, [k]: Math.max(0, Math.min(100, Math.round(v))) };
            try {
                localStorage.setItem("krithios:sliders", JSON.stringify(next));
            } catch {
                /* in-memory */
            }
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setSliders(() => {
            try {
                localStorage.setItem("krithios:sliders", JSON.stringify(SLIDER_DEFAULTS));
            } catch {
                /* in-memory */
            }
            return { ...SLIDER_DEFAULTS };
        });
        play("click");
        push("system", "Settings reset", "personality restored to factory defaults. the tabs remain.");
    }, [push]);

    const modes = useMemo<Modes>(
        () => ({
            chaotic: sliders.curiosity >= HIGH && sliders.overthinking >= HIGH,
            bedtime: sliders.curiosity >= HIGH && sliders.sleep >= HIGH,
            impatient: sliders.overthinking >= HIGH && sliders.tolerance <= LOW,
        }),
        [sliders],
    );

    const prevModes = useRef<Modes>(modes);
    useEffect(() => {
        const keys = Object.keys(modes) as (keyof Modes)[];
        const fresh = keys.filter((k) => modes[k] && !prevModes.current[k]);
        prevModes.current = modes;
        if (fresh.length === 0) return;
        unlock("chaos_engineer");
        play("egg");
        if (modes.chaotic)
            push("mode", "CHAOTIC RESEARCH MODE", "curiosity and overthinking reached a dangerous agreement. extra tabs deployed.");
        if (modes.bedtime)
            push("mode", "Bedtime conflict detected", "curiosity wants one more tab. sleep optimization strongly disagrees.");
        if (modes.impatient)
            push("mode", "Performance impatience engaged", "a performance analysis panel has been generated. you're welcome.");
    }, [modes, push, unlock]);

    const api = useMemo(() => ({ sliders, setSlider, reset, modes }), [sliders, setSlider, reset, modes]);
    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useSettings(): SettingsApi {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useSettings must be used inside SettingsProvider");
    return ctx;
}
